import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Book, BookStatus } from '../interfaces.ts';
import { getBookById, createBook, updateBook, deleteBook, getStatuses } from '../services/apiService.ts';

const BookDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new' || id === undefined;

  const [bookData, setBookData] = useState<Omit<Book, 'id'> | Book>({
    name: '',
    status_code: '',
    taipei_notes: '',
    new_taipei_notes: '',
    other_notes: '',
  });
  const [statuses, setStatuses] = useState<BookStatus[]>([]);
  const [loading, setLoading] = useState(true); // 新增載入狀態

  useEffect(() => {
    const fetchBookAndStatuses = async () => {
      setLoading(true); // 開始載入
      try {
        const [fetchedStatuses, fetchedBook] = await Promise.all([
          getStatuses(),
          !isNew && id ? getBookById(parseInt(id)) : Promise.resolve(null)
        ]);

        setStatuses(fetchedStatuses);

        if (fetchedBook) {
          setBookData(fetchedBook);
        } else {
          // Set a default status code for new books if statuses are available
          if (fetchedStatuses.length > 0) {
            setBookData(prev => ({ ...prev, status_code: fetchedStatuses[0].status_code }));
          }
        }
      } catch (error) {
        console.error('取得書籍或狀態時發生錯誤:', error);
        // Optionally navigate back or show an error message
      } finally {
        setLoading(false); // 載入完成
      }
    };

    fetchBookAndStatuses();
  }, [id, isNew]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBookData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('提交表單，bookData:', bookData);
    try {
      let resultBook: Book;
      if (isNew) {
        // Ensure id is not sent for new books
        const { id, ...dataToCreate } = bookData;
        console.log('新增書籍，發送資料:', dataToCreate);
        resultBook = await createBook(dataToCreate);
        console.log('新增書籍成功，結果:', resultBook);
      } else if (id) {
        console.log(`更新書籍 (ID: ${id})，發送資料:`, bookData);
        resultBook = await updateBook(parseInt(id), bookData);
        console.log('更新書籍成功，結果:', resultBook);
      } else {
        console.warn('handleSubmit 在非新增/編輯模式下被呼叫，已返回。');
        return; // Should not happen
      }
      navigate('/', { state: { updatedBook: resultBook } });
    } catch (error) {
      console.error('儲存書籍時發生錯誤:', error);
      // 可以在這裡添加更多錯誤處理，例如顯示錯誤訊息給使用者
    }
  };

  const handleDelete = async () => {
    if (id && !isNew && window.confirm('您確定要刪除這本書嗎？')) {
      try {
        await deleteBook(parseInt(id));
        navigate('/', { state: { deletedBookId: parseInt(id) } });
      } catch (error) {
        console.error('刪除書籍時發生錯誤:', error);
      }
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>載入中...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h1>{isNew ? '新增書籍' : '編輯書籍'}</h1>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '500px' }}>
        <div>
          <label htmlFor="name" style={{ display: 'block', marginBottom: '5px' }}>書名:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={bookData.name}
            onChange={handleChange}
            style={{ width: '100%', padding: '8px' }}
          />
        </div>
        <div>
          <label htmlFor="status_code" style={{ display: 'block', marginBottom: '5px' }}>狀態:</label>
          <select
            id="status_code"
            name="status_code"
            value={bookData.status_code}
            onChange={handleChange}
            style={{ width: '100%', padding: '8px' }}
          >
            {statuses.map(status => (
              <option key={status.status_code} value={status.status_code}>
                {status.status_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="taipei_notes" style={{ display: 'block', marginBottom: '5px' }}>台北圖書館備註:</label>
          <textarea
            id="taipei_notes"
            name="taipei_notes"
            value={bookData.taipei_notes}
            onChange={handleChange}
            rows={4}
            style={{ width: '100%', padding: '8px' }}
          ></textarea>
        </div>
        <div>
          <label htmlFor="new_taipei_notes" style={{ display: 'block', marginBottom: '5px' }}>新北圖書館備註:</label>
          <textarea
            id="new_taipei_notes"
            name="new_taipei_notes"
            value={bookData.new_taipei_notes}
            onChange={handleChange}
            rows={4}
            style={{ width: '100%', padding: '8px' }}
          ></textarea>
        </div>
        <div>
          <label htmlFor="other_notes" style={{ display: 'block', marginBottom: '5px' }}>其他備註:</label>
          <textarea
            id="other_notes"
            name="other_notes"
            value={bookData.other_notes}
            onChange={handleChange}
            rows={4}
            style={{ width: '100%', padding: '8px' }}
          ></textarea>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" style={{ padding: '10px 15px', cursor: 'pointer' }}>
            儲存
          </button>
          {!isNew && (
            <button type="button" onClick={handleDelete} style={{ padding: '10px 15px', cursor: 'pointer', backgroundColor: 'red', color: 'white' }}>
              刪除
            </button>
          )}
          <button type="button" onClick={() => navigate('/', { state: { cancelled: true, bookId: isNew ? undefined : (bookData as Book).id } })} style={{ padding: '10px 15px', cursor: 'pointer' }}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookDetailPage;